// libraries
import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Stack, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useGridApiRef } from '@mui/x-data-grid-pro'

// generic components
import BasicDataGrid from 'components/generic/datagrid'

// helpers and utils
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { setDecimalPrecision } from 'utils/helpers'
import { PAGE_SIZE } from 'utils/constants'

const useStyles = makeStyles(theme => ({
  root: {
    border: 'none',
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(1),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.Mui-selected': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
      '&:not(.Mui-selected)': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}))

const FreightLaneBladePriceHistory = ({ freightLanePricesData }) => {
  const classes = useStyles()
  const apiRef = useGridApiRef()

  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const { settings, defaultYardUm } = useSelector(state => state.settings)
  const { disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}

  const rows = useMemo(() => {
    const today = new Date()
    return (
      freightLanePricesData?.Items?.filter(
        price => !!price.expirationDate && new Date(price.expirationDate) < today,
      )?.sort((a, b) => new Date(b.expirationDate) - new Date(a.expirationDate)) || []
    )
  }, [freightLanePricesData])

  const columns = useMemo(
    () => [
      {
        field: 'effectiveDate',
        headerName: 'Effective Date',
        flex: 1,
        sortable: false,
        valueGetter: params => dateFormatter(params.value),
      },
      {
        field: 'expirationDate',
        headerName: 'Expiration Date',
        flex: 1,
        sortable: false,
        valueGetter: params => dateFormatter(params.value),
      },
      {
        field: 'minCost',
        headerName: 'Min Price',
        flex: 1,
        sortable: false,
        headerAlign: 'right',
        align: 'right',
        valueGetter: params => {
          const { minCost = 0 } = params.row
          return `$${setDecimalPrecision(minCost, { decimalPrecisionOverride: 2 })}`
        },
      },
      {
        field: 'costPerUm',
        headerName: 'Price/UM',
        flex: 1,
        sortable: false,
        headerAlign: 'right',
        align: 'right',
        valueGetter: params => {
          const { costPerUm = 0, um } = params.row
          return `$${setDecimalPrecision(costPerUm, {
            decimalPrecisionOverride: 2,
          })}/${disableUmDropdowns ? defaultYardUm : um}`
        },
      },
      {
        field: 'fuelSurcharge',
        headerName: 'Fuel Surcharge',
        flex: 1,
        sortable: false,
        headerAlign: 'right',
        align: 'right',
        valueGetter: params => {
          const { fuelSurcharge = 0 } = params.row
          return `${fuelSurcharge}%`
        },
      },
    ],
    [apiRef, disableUmDropdowns, defaultYardUm],
  )

  return (
    <Stack mt={5}>
      <Typography variant="h6" gutterBottom>
        Price History
      </Typography>
      <BasicDataGrid
        className={classes.root}
        density="compact"
        pageSize={pageSize}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        rowsPerPageOptions={[10, 20, 30]}
        pagination
        rows={rows}
        columns={columns}
        apiRef={apiRef}
        disableColumnMenu
        hideFooterSelectedRowCount
      />
    </Stack>
  )
}

FreightLaneBladePriceHistory.propTypes = {
  freightLanePricesData: PropTypes.object,
}

FreightLaneBladePriceHistory.defaultProps = {
  freightLanePricesData: null,
}

export default FreightLaneBladePriceHistory
